import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { WhiteboardCanvas } from '../components/training/WhiteboardCanvas';
import { WhiteboardToolbar, WB_COLORS, WB_WIDTHS, WB_ERASER_SIZES } from '../components/training/WhiteboardToolbar';
import { useWhiteboardHistory } from '../components/training/useWhiteboardHistory';
import { loadDraft, saveDraft, removeDraft } from '../components/training/whiteboardDraft';
import { useInkRecognition } from '../components/training/useInkRecognition';
import { useInkSettings } from '../components/training/useInkSettings';
import { InkSuggestionChip } from '../components/training/InkSuggestionChip';
import { loadTextPrefs, saveTextPrefs } from '../components/training/whiteboardFonts';
import { getUserFromToken } from '../utils/adminApi';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import './TrainingWhiteboard.css';

const BACK_TARGETS = {
  admin: { path: '/admin', label: 'Admin' },
  hr: { path: '/admin/hr/dashboard', label: 'HR Portal' },
  it: { path: '/admin/it-dashboard', label: 'IT Dashboard' },
  dashboard: { path: '/dashboard', label: 'Dashboard' },
};

const ConfirmDialog = ({ open, onOpenChange, testId, title, description, cancelLabel, confirmLabel, confirmClass, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid={testId}>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testId}-cancel`}>{cancelLabel}</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testId}-confirm`} className={confirmClass} onClick={onConfirm}>
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function TrainingWhiteboard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const back = BACK_TARGETS[params.get('from')] || BACK_TARGETS.dashboard;
  const userId = getUserFromToken()?.id;

  const history = useWhiteboardHistory();
  const [tool, setTool] = useState('pen');
  const [textSize, setTextSize] = useState(() => loadTextPrefs(userId).size); // WB-TEXT-EDIT-001: independent of pen thickness
  const [textFont, setTextFont] = useState(() => loadTextPrefs(userId).font); // WB-TEXT-FMT-001
  useEffect(() => { saveTextPrefs(userId, { font: textFont, size: textSize }); }, [userId, textFont, textSize]);
  const [color, setColor] = useState(WB_COLORS[0].hex);
  const [width, setWidth] = useState(WB_WIDTHS[1].px);
  const [eraserSize, setEraserSize] = useState(WB_ERASER_SIZES[1].px);
  const [showGrid, setShowGrid] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const restoredRef = useRef(false);
  const dirtyRef = useRef(false);
  const skipStatusRef = useRef(1);

  const hasContent = history.items.length > 0;

  // WB-INK-001 — Ink-to-Text (per-user, OFF by default, suggestion-only)
  const inkSettings = useInkSettings();
  const ink = useInkRecognition({ items: history.items, tool, enabled: inkSettings.enabled, autoConvert: inkSettings.auto_convert, history, textSize, textFont });
  const toggleInk = useCallback(() => {
    if (!inkSettings.enabled && !inkSettings.notice_acknowledged) { setDialog('inkNotice'); return; }
    inkSettings.save(!inkSettings.enabled, false);
  }, [inkSettings]);
  const toggleInkAuto = useCallback(() => inkSettings.save(true, !inkSettings.auto_convert), [inkSettings]);
  const handleCommit = useCallback((item) => { history.push(item); ink.noteStroke(item); }, [history.push, ink.noteStroke]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const draft = loadDraft(userId);
    if (draft && draft.items.length) {
      history.hydrate(draft.items);
      if (draft.color) setColor(draft.color);
      if (draft.width) setWidth(draft.width);
      if (draft.eraserSize) setEraserSize(draft.eraserSize);
      if (typeof draft.showGrid === 'boolean') setShowGrid(draft.showGrid);
      setSaveStatus('restored');
      skipStatusRef.current = 2;
      toast.success('Restored your previous whiteboard session', { id: 'wb-restored' });
    }
    restoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const persist = useCallback((silent = false) => {
    if (!restoredRef.current) return;
    const result = saveDraft(userId, { items: history.items, color, width, eraserSize, showGrid });
    dirtyRef.current = false;
    if (silent) return;
    setSaveStatus(result === 'saved' ? 'saved' : result === 'empty' ? 'idle' : result);
  }, [userId, history.items, color, width, eraserSize, showGrid]);

  useEffect(() => {
    if (!restoredRef.current) return undefined;
    dirtyRef.current = true;
    const silent = skipStatusRef.current > 0;
    if (silent) skipStatusRef.current -= 1;
    if (!silent && hasContent) setSaveStatus('pending');
    const t = setTimeout(() => persist(silent), 800);
    return () => clearTimeout(t);
  }, [persist, hasContent]);

  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevGutter = document.documentElement.style.scrollbarGutter;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.scrollbarGutter = 'auto';
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.scrollbarGutter = prevGutter;
    };
  }, []);

  useEffect(() => {
    const flush = () => { if (dirtyRef.current) persist(); };
    const warn = (e) => {
      flush();
      if (hasContent) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', warn);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('beforeunload', warn);
      window.removeEventListener('pagehide', flush);
    };
  }, [hasContent, persist]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'textarea' || tag === 'input' || dialog) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); history.undo(); return; }
      if ((mod && e.key.toLowerCase() === 'y') || (mod && e.shiftKey && e.key.toLowerCase() === 'z')) { e.preventDefault(); history.redo(); return; }
      if (mod) return;
      const k = e.key.toLowerCase();
      if (k === 'p') setTool('pen');
      else if (k === 'e') setTool('eraser');
      else if (k === 't') setTool('text');
      else if (k === 'g') setShowGrid((v) => !v);
      else if (k === '[') setWidth((w) => WB_WIDTHS[Math.max(0, WB_WIDTHS.findIndex((x) => x.px === w) - 1)].px);
      else if (k === ']') setWidth((w) => WB_WIDTHS[Math.min(WB_WIDTHS.length - 1, WB_WIDTHS.findIndex((x) => x.px === w) + 1)].px);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [history, dialog]);

  const leave = useCallback(() => {
    if (dirtyRef.current) persist();
    navigate(back.path);
  }, [persist, navigate, back.path]);

  const handleBack = useCallback(() => {
    if (hasContent) setDialog('leave');
    else navigate(back.path);
  }, [hasContent, navigate, back.path]);

  const startNew = useCallback(() => {
    history.reset();
    removeDraft(userId);
    dirtyRef.current = false;
    setSaveStatus('idle');
    setDialog(null);
    toast.success('Started a new whiteboard');
  }, [history, userId]);

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-white" data-testid="training-whiteboard-page">
      <WhiteboardToolbar
        tool={tool} setTool={setTool}
        color={color} setColor={setColor}
        width={width} setWidth={setWidth}
        eraserSize={eraserSize} setEraserSize={setEraserSize}
        textSize={textSize} setTextSize={setTextSize}
        textFont={textFont} setTextFont={setTextFont}
        showGrid={showGrid} setShowGrid={setShowGrid}
        canUndo={history.canUndo} canRedo={history.canRedo}
        onUndo={history.undo} onRedo={history.redo}
        onClear={() => setDialog('clear')}
        onStartNew={() => setDialog('startNew')}
        hasContent={hasContent}
        saveStatus={saveStatus}
        onBack={handleBack} backLabel={back.label}
        inkEnabled={inkSettings.enabled} onToggleInk={toggleInk}
        inkAutoConvert={inkSettings.auto_convert} onToggleInkAuto={toggleInkAuto} inkBusy={ink.busy}
      />
      <WhiteboardCanvas
        items={history.items}
        tool={tool}
        color={color}
        width={width}
        eraserSize={eraserSize}
        textSize={textSize}
        textFont={textFont}
        showGrid={showGrid}
        onCommit={handleCommit}
        onStrokeStart={ink.noteStrokeStart}
        onUpdate={history.update}
        onRemove={history.remove}
        onSelectText={(item) => { setColor(item.color); setTextSize(item.size); setTextFont(item.font || 'clean'); }}
        inkBox={ink.suggestion?.box || null}
        flashTextId={ink.flashTextId}
        renderOverlay={(size) => <InkSuggestionChip ink={ink} canvasSize={size} />}
      />

      <ConfirmDialog
        open={dialog === 'clear'} onOpenChange={(o) => !o && setDialog(null)} testId="wb-clear-dialog"
        title="Clear the whiteboard?" description="Everything on the canvas will be removed. You can still undo this once."
        cancelLabel="Cancel" confirmLabel="Clear all" confirmClass="bg-red-600 hover:bg-red-700"
        onConfirm={() => { history.clear(); setDialog(null); }}
      />
      <ConfirmDialog
        open={dialog === 'startNew'} onOpenChange={(o) => !o && setDialog(null)} testId="wb-start-new-dialog"
        title="Start a new whiteboard?"
        description="This removes the current board and its locally saved draft. This cannot be undone."
        cancelLabel="Keep current board" confirmLabel="Start new" confirmClass="bg-red-600 hover:bg-red-700"
        onConfirm={startNew}
      />
      <ConfirmDialog
        open={dialog === 'leave'} onOpenChange={(o) => !o && setDialog(null)} testId="wb-leave-dialog"
        title="Leave Whiteboard?"
        description="Your current whiteboard has been automatically saved on this device. You can continue from where you left off when you return."
        cancelLabel="Stay on Whiteboard" confirmLabel="Leave Whiteboard" confirmClass="bg-blue-600 hover:bg-blue-700"
        onConfirm={leave}
      />
      <ConfirmDialog
        open={dialog === 'inkNotice'} onOpenChange={(o) => !o && setDialog(null)} testId="wb-ink-notice-dialog"
        title="Turn on Ink-to-Text (AI)?"
        description="When you pause after writing with the Pen, a cropped image of just that handwriting — never the whole board — is sent to an AI recognition service (Google Gemini via Emergent) to suggest typed text. Nothing on your board changes until you accept a suggestion. Images and recognized text are not stored; only anonymous usage metrics are kept. You can turn this off at any time."
        cancelLabel="Not now" confirmLabel="Enable Ink-to-Text" confirmClass="bg-blue-600 hover:bg-blue-700"
        onConfirm={() => { setDialog(null); inkSettings.save(true, false); }}
      />
    </div>
  );
}
